// src/screens/yonetim/MusteriYonetimScreen.js
import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  View, Text, StyleSheet, TextInput, FlatList, TouchableOpacity,
  ActivityIndicator, Switch, ScrollView, Alert
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import CustomAlert from '../../components/CustomAlert';
import CONFIG from '../../config/Config';
import { AuthContext } from '../../context/AuthContext';

const FormInput = ({ label, icon, value, onChangeText, placeholder, keyboardType, multiline, editable }) => (
  <View style={styles.formGroup}>
    <Text style={styles.formLabel}>
      <Ionicons name={icon} size={13} color="#7f8c8d" />{"  "}{label}
    </Text>
    <TextInput
      style={[
        styles.formInput,
        multiline && styles.formInputMultiline,
        editable === false && styles.formInputDisabled,
      ]}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder || label}
      placeholderTextColor="#bdc3c7"
      keyboardType={keyboardType || 'default'}
      multiline={multiline}
      numberOfLines={multiline ? 3 : 1}
      editable={editable !== false}
    />
  </View>
);

export default function MusteriYonetimScreen() {
  const navigation = useNavigation();

  const [musteriler, setMusteriler]       = useState([]);
  const [isLoading, setIsLoading]         = useState(false);
  const [searchMusteri, setSearchMusteri] = useState('');
  const [seciliMusteri, setSeciliMusteri] = useState(null); // null = liste
  const [isYeniMusteri, setIsYeniMusteri] = useState(false);
  const [isSaving, setIsSaving]           = useState(false);

  // Form
  const [formCariID, setFormCariID]               = useState('');
  const [formAdSoyad, setFormAdSoyad]             = useState('');
  const [formTelefon, setFormTelefon]             = useState('');
  const [formAdres, setFormAdres]                 = useState('');
  const [formAcikHesap, setFormAcikHesap]         = useState(false);
  const [formIndirimOrani, setFormIndirimOrani]   = useState('');

  // Alertler
  const [showSuccess, setShowSuccess]   = useState(false);
  const [showError, setShowError]       = useState(false);
  const [errorMsg, setErrorMsg]         = useState('');

  const { userToken } = useContext(AuthContext);
  const userInfo = userToken ? JSON.parse(userToken) : null;
  const userLevel = userInfo?.Level || 0;

  // Level < 3 ise indirim ve açık hesap alanları kilitlenir
  const canEditIndirimAcikHesap = userLevel >= 3;

  useEffect(() => {
    fetchMusteriler();
  }, []);

  // Header back button — form açıksa listeye dön
  useEffect(() => {
    navigation.setOptions({
      headerLeft: seciliMusteri
        ? () => (
            <TouchableOpacity onPress={() => setSeciliMusteri(null)} style={{ marginLeft: 4 }}>
              <Ionicons name="arrow-back" size={24} color="#2c3e50" />
            </TouchableOpacity>
          )
        : undefined,
      title: !seciliMusteri ? 'Müşteri Yönetimi' : isYeniMusteri ? 'Yeni Müşteri' : 'Müşteri Düzenle',
    });
  }, [seciliMusteri, isYeniMusteri]);

  const fetchMusteriler = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/cari`, { headers: { 'X-App-Env': CONFIG.ENV } });
      const result = await response.json();
      if (result.success) setMusteriler(result.data);
      else Alert.alert("Uyarı", result.message);
    } catch {
      Alert.alert("Hata", "Veri çekilemedi.");
    } finally {
      setIsLoading(false);
    }
  };

  const formTemizle = () => {
    setFormCariID(''); setFormAdSoyad(''); setFormTelefon('');
    setFormAdres(''); setFormAcikHesap(false); setFormIndirimOrani('');
  };

  const musteriSec = (musteri) => {
    setIsYeniMusteri(false);
    setFormCariID(musteri.CariID?.toString() || '');
    setFormAdSoyad(musteri.Isim || '');
    setFormTelefon(musteri.Telefon || '');
    setFormAdres(musteri.Adres || '');
    setFormAcikHesap(musteri.AcikHesap === 1);
    setFormIndirimOrani(musteri.Indirim?.toString() || '');
    setSeciliMusteri(musteri);
  };

  const yeniMusteriAc = () => {
    setIsYeniMusteri(true);
    formTemizle();
    setSeciliMusteri({});
  };

  const musteriKaydet = async () => {
    if (!formAdSoyad.trim()) { setErrorMsg("Ad Soyad alanı zorunludur!"); setShowError(true); return; }

    setIsSaving(true);
    try {
      const payload = {
        Isim:      formAdSoyad.trim(),
        Telefon:   formTelefon.trim(),
        Adres:     formAdres.trim(),
        // Level < 3 ise indirim ve açık hesap zorla sıfırlanır / mevcut değer korunmaz
        AcikHesap: canEditIndirimAcikHesap ? (formAcikHesap ? 1 : 0) : 0,
        Indirim:   canEditIndirimAcikHesap ? (parseFloat(formIndirimOrani) || 0) : 0,
      };
      const url    = isYeniMusteri ? `${CONFIG.API_BASE_URL}/cari` : `${CONFIG.API_BASE_URL}/cari/${formCariID}`;
      const method = isYeniMusteri ? 'POST' : 'PUT';

      const response = await fetch(url, { method, headers: { 'Content-Type': 'application/json', 'X-App-Env': CONFIG.ENV }, body: JSON.stringify(payload) });
      const result = await response.json();

      if (result.success) {
        setSeciliMusteri(null);
        setShowSuccess(true);
        fetchMusteriler();
      } else {
        setErrorMsg(result.message || "İşlem başarısız.");
        setShowError(true);
      }
    } catch {
      setErrorMsg("Sunucuya bağlanılamadı.");
      setShowError(true);
    } finally {
      setIsSaving(false);
    }
  };

  // ── LİSTE ───────────────────────────────────────────────────
  if (!seciliMusteri) {
    return (
      <SafeAreaView style={styles.safeArea} edges={['left', 'right', 'bottom']}>
        <View style={styles.searchRow}>
          <TextInput
            style={[styles.searchInput, { flex: 1 }]}
            placeholder="Müşteri Ara (İsim)..."
            placeholderTextColor="#95a5a6"
            value={searchMusteri}
            onChangeText={setSearchMusteri}
          />
          <TouchableOpacity style={styles.yeniBtn} onPress={yeniMusteriAc}>
            <Ionicons name="person-add" size={20} color="#fff" />
          </TouchableOpacity>
        </View>

        {isLoading ? (
          <View style={styles.loadingBox}>
            <ActivityIndicator size="large" color="#9C27B0" />
            <Text style={styles.loadingText}>Müşteriler getiriliyor...</Text>
          </View>
        ) : (
          <FlatList
            keyboardShouldPersistTaps="handled"
            data={musteriler.filter(m => m.Isim?.toLowerCase().includes(searchMusteri.toLowerCase()))}
            keyExtractor={item => item.CariID.toString()}
            contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 40 }}
            renderItem={({ item }) => (
              <TouchableOpacity style={styles.musteriItem} onPress={() => musteriSec(item)}>
                <View style={styles.avatar}>
                  <Text style={styles.avatarText}>{item.Isim?.charAt(0)?.toUpperCase() || '?'}</Text>
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.musteriAd} numberOfLines={1}>{item.Isim}</Text>
                  <View style={{ flexDirection: 'row', gap: 6, marginTop: 3 }}>
                    {item.Telefon ? <Text style={styles.altBilgi}><Ionicons name="call-outline" size={11} /> {item.Telefon}</Text> : null}
                    {item.AcikHesap === 1 && <View style={styles.acikBadge}><Text style={styles.acikBadgeText}>Açık Hesap</Text></View>}
                    {item.Indirim > 0 && <View style={styles.indirimBadge}><Text style={styles.indirimBadgeText}>%{item.Indirim}</Text></View>}
                  </View>
                </View>
                <Ionicons name="create-outline" size={20} color="#9C27B0" />
              </TouchableOpacity>
            )}
            ListEmptyComponent={
              <View style={{ alignItems: 'center', paddingTop: 60 }}>
                <Ionicons name="people-outline" size={48} color="#bdc3c7" />
                <Text style={styles.emptyText}>Müşteri bulunamadı.</Text>
              </View>
            }
          />
        )}

        {showSuccess && (
          <CustomAlert visible={showSuccess} title="Başarılı"
            message={isYeniMusteri ? "Yeni müşteri eklendi." : "Müşteri güncellendi."}
            type="success" confirmText="Tamam" onConfirm={() => setShowSuccess(false)} />
        )}
      </SafeAreaView>
    );
  }

  // ── FORM ────────────────────────────────────────────────────
  return (
    <SafeAreaView style={styles.safeArea} edges={['left', 'right', 'bottom']}>
      <ScrollView contentContainerStyle={styles.formContainer} keyboardShouldPersistTaps="handled">

        {!isYeniMusteri && (
          <View style={styles.cariIdKutu}>
            <Ionicons name="finger-print-outline" size={14} color="#9C27B0" />
            <Text style={styles.cariIdText}>Cari ID: <Text style={{ fontWeight: 'bold' }}>{formCariID}</Text></Text>
          </View>
        )}

        <FormInput label="Ad Soyad *"         icon="person-outline"   value={formAdSoyad}      onChangeText={setFormAdSoyad}      placeholder="Müşteri adını girin" />
        <FormInput label="Telefon"            icon="call-outline"     value={formTelefon}      onChangeText={setFormTelefon}      placeholder="05XX XXX XX XX" keyboardType="phone-pad" />
        <FormInput label="Adres"              icon="location-outline" value={formAdres}        onChangeText={setFormAdres}        placeholder="Adres" multiline />

        {/* İndirim Oranı — Level < 3 ise kilitli */}
        <FormInput
          label={`İndirim Oranı (%)${!canEditIndirimAcikHesap ? ' 🔒' : ''}`}
          icon="pricetag-outline"
          value={canEditIndirimAcikHesap ? formIndirimOrani : '0'}
          onChangeText={setFormIndirimOrani}
          placeholder="0"
          keyboardType="numeric"
          editable={canEditIndirimAcikHesap}
        />

        {/* Açık Hesap — Level < 3 ise kilitli */}
        <View style={[styles.toggleRow, !canEditIndirimAcikHesap && styles.toggleRowDisabled]}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Ionicons name="book-outline" size={16} color={canEditIndirimAcikHesap ? "#7f8c8d" : "#bdc3c7"} />
            <Text style={[styles.toggleLabel, !canEditIndirimAcikHesap && styles.toggleLabelDisabled]}>
              {"  "}Açık Hesap (Cari){!canEditIndirimAcikHesap ? ' 🔒' : ''}
            </Text>
          </View>
          <Switch
            value={canEditIndirimAcikHesap ? formAcikHesap : false}
            onValueChange={canEditIndirimAcikHesap ? setFormAcikHesap : undefined}
            disabled={!canEditIndirimAcikHesap}
            trackColor={{ false: '#e0e0e0', true: '#9C27B0' }}
            thumbColor={canEditIndirimAcikHesap && formAcikHesap ? '#7B1FA2' : '#f4f3f4'}
          />
        </View>

        {!canEditIndirimAcikHesap && (
          <View style={styles.yetkiUyari}>
            <Ionicons name="lock-closed-outline" size={13} color="#e67e22" />
            <Text style={styles.yetkiUyariText}>  İndirim oranı ve açık hesap ayarları için yetkiniz yok.</Text>
          </View>
        )}

        <TouchableOpacity style={[styles.kaydetBtn, isSaving && { opacity: 0.6 }]} onPress={musteriKaydet} disabled={isSaving}>
          {isSaving
            ? <ActivityIndicator color="#fff" />
            : <>
                <Ionicons name={isYeniMusteri ? "person-add-outline" : "save-outline"} size={20} color="#fff" />
                <Text style={styles.kaydetBtnText}>{isYeniMusteri ? 'Müşteri Ekle' : 'Değişiklikleri Kaydet'}</Text>
              </>
          }
        </TouchableOpacity>

        <View style={{ height: 40 }} />
      </ScrollView>

      {showError && (
        <CustomAlert visible={showError} title="Uyarı" message={errorMsg} type="error" confirmText="Tamam" onConfirm={() => setShowError(false)} />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea:            { flex: 1, backgroundColor: '#F5F7FA' },
  searchRow:           { flexDirection: 'row', alignItems: 'center', margin: 16, gap: 10 },
  searchInput:         { backgroundColor: '#fff', borderRadius: 12, padding: 12, fontSize: 15, color: '#2c3e50', borderWidth: 1, borderColor: '#e9ecef' },
  yeniBtn:             { backgroundColor: '#9C27B0', width: 46, height: 46, borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  loadingBox:          { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText:         { marginTop: 10, color: '#7f8c8d', fontSize: 15 },
  musteriItem:         { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#ecf0f1' },
  avatar:              { width: 42, height: 42, borderRadius: 21, backgroundColor: '#EDE7F6', justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  avatarText:          { fontSize: 18, fontWeight: 'bold', color: '#9C27B0' },
  musteriAd:           { fontSize: 15, fontWeight: '600', color: '#34495e' },
  altBilgi:            { fontSize: 12, color: '#95a5a6' },
  acikBadge:           { backgroundColor: '#EDE7F6', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 },
  acikBadgeText:       { fontSize: 10, fontWeight: 'bold', color: '#9C27B0' },
  indirimBadge:        { backgroundColor: '#FFF3E0', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 },
  indirimBadgeText:    { fontSize: 10, fontWeight: 'bold', color: '#FF9800' },
  emptyText:           { textAlign: 'center', color: '#95a5a6', marginTop: 12, fontSize: 15 },
  formContainer:       { padding: 16 },
  cariIdKutu:          { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3E5F5', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8, marginBottom: 16, gap: 6 },
  cariIdText:          { fontSize: 13, color: '#7B1FA2' },
  formGroup:           { marginBottom: 16 },
  formLabel:           { fontSize: 13, color: '#7f8c8d', fontWeight: '600', marginBottom: 6 },
  formInput:           { backgroundColor: '#fff', borderWidth: 1, borderColor: '#e9ecef', borderRadius: 10, padding: 12, fontSize: 15, color: '#2c3e50' },
  formInputMultiline:  { minHeight: 80, textAlignVertical: 'top' },
  formInputDisabled:   { backgroundColor: '#f5f5f5', color: '#bdc3c7', borderColor: '#ececec' },
  toggleRow:           { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fff', borderWidth: 1, borderColor: '#e9ecef', borderRadius: 10, padding: 12, marginBottom: 12 },
  toggleRowDisabled:   { backgroundColor: '#fafafa', borderColor: '#ececec' },
  toggleLabel:         { fontSize: 14, color: '#34495e', fontWeight: '500' },
  toggleLabelDisabled: { color: '#bdc3c7' },
  yetkiUyari:          { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFF8F0', borderWidth: 1, borderColor: '#fde8c8', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8, marginBottom: 20 },
  yetkiUyariText:      { fontSize: 12, color: '#e67e22' },
  kaydetBtn:           { backgroundColor: '#9C27B0', borderRadius: 12, paddingVertical: 15, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 8 },
  kaydetBtnText:       { fontSize: 16, fontWeight: 'bold', color: '#fff' },
});